import React, { useEffect, useState, useContext } from 'react';
import {
    Box, Flex, Text, Button, Input, VStack, HStack, Icon,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalCloseButton, useDisclosure, Badge, Divider, Spinner,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Tag, TagLabel, useToast, Switch, FormLabel, FormControl,
    IconButton, Tooltip, Heading, SimpleGrid, Card, CardBody,
    CardHeader, Tabs, TabList, TabPanels, Tab, TabPanel, Select,
    NumberInput, NumberInputField, NumberInputStepper,
    NumberIncrementStepper, NumberDecrementStepper
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdDownload, MdVisibility, MdClose, MdCalendarMonth, MdCopyAll, MdRefresh, MdToggleOn, MdToggleOff } from 'react-icons/md';
import * as XLSX from 'xlsx';
import axiosInstance from '../services/axiosInstance';
import LayoutDashboard from '../shared/LayoutDashboard';

const PRIMARY = '#F2C919';
const DARK = '#2C3E50';

export default function AdminScheduleForm() {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    // ── Form creation state ─────────────────────────────────────────────────
    const [matiere, setMatiere] = useState('');
    const [session1Options, setSession1Options] = useState(['']);
    const [session2Options, setSession2Options] = useState(['']);
    const [creating, setCreating] = useState(false);

    // ── Forms list ──────────────────────────────────────────────────────────
    const [forms, setForms] = useState([]);
    const [loadingForms, setLoadingForms] = useState(true);

    // ── Responses modal ─────────────────────────────────────────────────────
    const [selectedForm, setSelectedForm] = useState(null);
    const [responses, setResponses] = useState([]);
    const [loadingResponses, setLoadingResponses] = useState(false);

    // ── Courses list (for matière selector) ─────────────────────────────
    const [courses, setCourses] = useState([]);

    const token = localStorage.getItem('userToken');
    const headers = { Authorization: `Bearer ${token}` };

    // ── Fetch all forms ─────────────────────────────────────────────────────
    const fetchForms = async () => {
        setLoadingForms(true);
        try {
            const { data } = await axiosInstance.get('/schedule-forms', { headers });
            setForms(data.data || []);
        } catch (e) {
            toast({ title: 'خطأ في تحميل الاستمارات', status: 'error', duration: 3000 });
        } finally {
            setLoadingForms(false);
        }
    };

    useEffect(() => {
        fetchForms();
        // Fetch all courses for the matière selector
        axiosInstance.get('/courses', { headers })
            .then(res => {
                const data = res.data;
                let list = Array.isArray(data) ? data : (data.courses || data.data || []);
                // Sort alphabetically by name
                list.sort((a, b) => a.name.localeCompare(b.name));
                setCourses(list);
            })
            .catch(err => {
                console.error("Error fetching courses:", err);
            });
    }, []);

    // ── Session option helpers ──────────────────────────────────────────────
    const updateOption = (setter, list, idx, val) => {
        const updated = [...list];
        updated[idx] = val;
        setter(updated);
    };
    const addOption = (setter, list) => setter([...list, '']);
    const removeOption = (setter, list, idx) => setter(list.filter((_, i) => i !== idx));

    // ── Create form ─────────────────────────────────────────────────────────
    const handleCreate = async () => {
        const s1 = session1Options.filter(o => o.trim());
        const s2 = session2Options.filter(o => o.trim());
        if (!matiere.trim() || !s1.length || !s2.length) {
            return toast({ title: 'يرجى ملء جميع الحقول', status: 'warning', duration: 3000 });
        }
        setCreating(true);
        try {
            await axiosInstance.post('/schedule-forms', { matiere: matiere.trim(), session1Options: s1, session2Options: s2 }, { headers });
            toast({ title: 'تم إنشاء الاستمارة بنجاح ✅', status: 'success', duration: 3000 });
            setMatiere('');
            setSession1Options(['']);
            setSession2Options(['']);
            fetchForms();
        } catch (e) {
            toast({ title: 'فشل الإنشاء', description: e.response?.data?.error, status: 'error', duration: 3000 });
        } finally {
            setCreating(false);
        }
    };

    // ── Delete form ─────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه الاستمارة؟')) return;
        try {
            await axiosInstance.delete(`/schedule-forms/${id}`, { headers });
            toast({ title: 'تم الحذف', status: 'success', duration: 2000 });
            fetchForms();
        } catch (e) {
            toast({ title: 'فشل الحذف', status: 'error', duration: 3000 });
        }
    };

    // ── Toggle active ───────────────────────────────────────────────────────
    const handleToggle = async (id) => {
        try {
            await axiosInstance.patch(`/schedule-forms/${id}/toggle`, {}, { headers });
            fetchForms();
        } catch (e) {
            toast({ title: 'فشل التحديث', status: 'error', duration: 3000 });
        }
    };

    // ── View responses ──────────────────────────────────────────────────────
    const handleViewResponses = async (form) => {
        setSelectedForm(form);
        setLoadingResponses(true);
        onOpen();
        try {
            const { data } = await axiosInstance.get(`/schedule-forms/${form._id}/responses`, { headers });
            setResponses(data.data || []);
        } catch (e) {
            toast({ title: 'فشل تحميل الردود', status: 'error', duration: 3000 });
        } finally {
            setLoadingResponses(false);
        }
    };

    // ── Group responses by session combo ───────────────────────────────────
    const groupedResponses = () => {
        const groups = {};
        responses.forEach(r => {
            const key = `${r.session1Choice} | ${r.session2Choice}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push(r);
        });
        return groups;
    };

    // ── Download Excel (client-side, grouped) ──────────────────────────────
    const handleDownloadExcel = async (form) => {
        try {
            const { data } = await axiosInstance.get(`/schedule-forms/${form._id}/responses`, { headers });
            const resps = data.data || [];

            const wb = XLSX.utils.book_new();
            if (!resps.length) {
                const ws = XLSX.utils.aoa_to_sheet([['لا توجد ردود']]);
                XLSX.utils.book_append_sheet(wb, ws, 'النتائج');
            } else {
                const groups = {};
                resps.forEach(r => {
                    const key = `${r.session1Choice} | ${r.session2Choice}`;
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(r);
                });

                const rows = [['المجموعة', 'الاسم و اللقب', 'رقم الواتساب', 'الحصة الأولى', 'الحصة الثانية', 'تاريخ الإرسال']];
                let gNum = 1;
                Object.entries(groups).forEach(([combo, members]) => {
                    const label = `مجموعة ${gNum++} (${combo})`;
                    members.forEach((r, i) => {
                        rows.push([i === 0 ? label : '', r.fullName, r.whatsappNumber, r.session1Choice, r.session2Choice,
                        new Date(r.submittedAt).toLocaleString('ar-TN')]);
                    });
                    rows.push([]);
                });

                const ws = XLSX.utils.aoa_to_sheet(rows);
                ws['!cols'] = [{ wch: 32 }, { wch: 28 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 22 }];
                XLSX.utils.book_append_sheet(wb, ws, 'النتائج');
            }

            XLSX.writeFile(wb, `schedule_${form.matiere}_${Date.now()}.xlsx`);
            toast({ title: 'تم تحميل الملف ✅', status: 'success', duration: 2000 });
        } catch (e) {
            toast({ title: 'فشل التحميل', status: 'error', duration: 3000 });
        }
    };

    // ── Copy link ───────────────────────────────────────────────────────────
    const copyLink = (id) => {
        const link = `${window.location.origin}/schedule/${id}`;
        navigator.clipboard.writeText(link);
        toast({ title: 'تم نسخ الرابط 📋', status: 'info', duration: 2000 });
    };

    const groups = selectedForm ? groupedResponses() : {};
    const groupColors = ['blue', 'green', 'purple', 'orange', 'teal', 'pink', 'cyan', 'red'];

    return (
        <LayoutDashboard>
            <Box dir="rtl" minH="100vh" bg="gray.50" p={{ base: 3, md: 6 }}>
                <HStack mb={6} justify="space-between">
                    <HStack>
                        <Icon as={MdCalendarMonth} boxSize={8} color={PRIMARY} />
                        <Heading size="lg" color={DARK}>استمارات جدول الأوقات</Heading>
                    </HStack>
                    <IconButton icon={<MdRefresh />} onClick={fetchForms} colorScheme="yellow" variant="ghost"
                        aria-label="تحديث" borderRadius="xl" isLoading={loadingForms} />
                </HStack>

                <Tabs colorScheme="yellow" variant="enclosed" borderRadius="xl">
                    <TabList bg="white" borderRadius="xl" p={1} shadow="sm" mb={4}>
                        <Tab fontWeight="600" fontSize="md" _selected={{ color: DARK, bg: PRIMARY, borderRadius: 'lg' }}>
                            ➕ إنشاء استمارة جديدة
                        </Tab>
                        <Tab fontWeight="600" fontSize="md" _selected={{ color: DARK, bg: PRIMARY, borderRadius: 'lg' }}>
                            📋 الاستمارات ({forms.length})
                        </Tab>
                    </TabList>

                    <TabPanels>
                        {/* ── Tab 1: Create ── */}
                        <TabPanel>
                            <Box bg="white" borderRadius="2xl" shadow="md" p={6} maxW="700px" mx="auto">
                                <VStack spacing={5} align="stretch">
                                    <FormControl>
                                        <FormLabel fontWeight="700" color={DARK}>المادة (Matière)</FormLabel>
                                        <Select
                                            value={matiere}
                                            onChange={e => setMatiere(e.target.value)}
                                            placeholder="-- اختر المادة --"
                                            borderRadius="xl"
                                            size="lg"
                                            focusBorderColor={PRIMARY}
                                        >
                                            {courses.map(c => (
                                                <option key={c._id} value={c.name}>{c.name}</option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Session 1 options */}
                                    <Box>
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontWeight="700" color={DARK}>خيارات الحصة الأولى</Text>
                                            <Button size="sm" leftIcon={<MdAdd />} onClick={() => addOption(setSession1Options, session1Options)}
                                                colorScheme="yellow" borderRadius="xl">إضافة خيار</Button>
                                        </HStack>
                                        <VStack spacing={2}>
                                            {session1Options.map((opt, idx) => (
                                                <HStack key={idx} w="100%">
                                                    <Input value={opt} onChange={e => updateOption(setSession1Options, session1Options, idx, e.target.value)}
                                                        placeholder={`الخيار ${idx + 1}`} borderRadius="xl" focusBorderColor={PRIMARY} />
                                                    {session1Options.length > 1 && (
                                                        <IconButton size="sm" icon={<MdClose />} colorScheme="red" variant="ghost"
                                                            borderRadius="xl" onClick={() => removeOption(setSession1Options, session1Options, idx)} />
                                                    )}
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>

                                    {/* Session 2 options */}
                                    <Box>
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontWeight="700" color={DARK}>خيارات الحصة الثانية</Text>
                                            <Button size="sm" leftIcon={<MdAdd />} onClick={() => addOption(setSession2Options, session2Options)}
                                                colorScheme="yellow" borderRadius="xl">إضافة خيار</Button>
                                        </HStack>
                                        <VStack spacing={2}>
                                            {session2Options.map((opt, idx) => (
                                                <HStack key={idx} w="100%">
                                                    <Input value={opt} onChange={e => updateOption(setSession2Options, session2Options, idx, e.target.value)}
                                                        placeholder={`الخيار ${idx + 1}`} borderRadius="xl" focusBorderColor={PRIMARY} />
                                                    {session2Options.length > 1 && (
                                                        <IconButton size="sm" icon={<MdClose />} colorScheme="red" variant="ghost"
                                                            borderRadius="xl" onClick={() => removeOption(setSession2Options, session2Options, idx)} />
                                                    )}
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>

                                    <Button
                                        colorScheme="yellow"
                                        size="lg"
                                        onClick={handleCreate}
                                        isLoading={creating}
                                        loadingText="جاري الإنشاء..."
                                        borderRadius="xl"
                                        bg={PRIMARY}
                                        color={DARK}
                                        fontWeight="700"
                                        _hover={{ bg: '#d4ac00' }}
                                        leftIcon={<MdAdd />}
                                    >
                                        إنشاء الاستمارة
                                    </Button>
                                </VStack>
                            </Box>
                        </TabPanel>

                        {/* ── Tab 2: Forms list ── */}
                        <TabPanel>
                            {loadingForms ? (
                                <Flex justify="center" py={10}><Spinner size="xl" color={PRIMARY} /></Flex>
                            ) : forms.length === 0 ? (
                                <Flex justify="center" py={10} direction="column" align="center" gap={3}>
                                    <Icon as={MdCalendarMonth} boxSize={16} color="gray.300" />
                                    <Text color="gray.400" fontSize="lg">لا توجد استمارات بعد</Text>
                                </Flex>
                            ) : (
                                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                                    {forms.map(form => (
                                        <Card key={form._id} borderRadius="2xl" shadow="md" borderTop="4px solid" borderTopColor={PRIMARY} overflow="hidden">
                                            <CardHeader pb={2}>
                                                <HStack justify="space-between">
                                                    <HStack>
                                                        <Icon as={MdCalendarMonth} color={PRIMARY} boxSize={5} />
                                                        <Text fontWeight="800" fontSize="lg" color={DARK}>{form.matiere}</Text>
                                                    </HStack>
                                                    <HStack>
                                                        <Badge colorScheme={form.isActive ? 'green' : 'gray'} borderRadius="full" px={2}>
                                                            {form.isActive ? 'نشطة' : 'مغلقة'}
                                                        </Badge>
                                                        <Tooltip label={form.isActive ? 'إغلاق' : 'تفعيل'}>
                                                            <IconButton size="xs" icon={form.isActive ? <MdToggleOn /> : <MdToggleOff />}
                                                                colorScheme={form.isActive ? 'green' : 'gray'} variant="ghost"
                                                                onClick={() => handleToggle(form._id)} borderRadius="lg" />
                                                        </Tooltip>
                                                    </HStack>
                                                </HStack>
                                                <Text color="gray.400" fontSize="sm">
                                                    {new Date(form.createdAt).toLocaleDateString('ar-TN')}
                                                </Text>
                                            </CardHeader>
                                            <CardBody pt={0}>
                                                <VStack align="stretch" spacing={3}>
                                                    <HStack>
                                                        <Badge colorScheme="blue" fontSize="sm" px={2} py={1} borderRadius="lg">
                                                            {form.responseCount} رد
                                                        </Badge>
                                                        <Badge colorScheme="purple" fontSize="sm" px={2} py={1} borderRadius="lg">
                                                            {form.session1Options?.length} خيار 1
                                                        </Badge>
                                                        <Badge colorScheme="orange" fontSize="sm" px={2} py={1} borderRadius="lg">
                                                            {form.session2Options?.length} خيار 2
                                                        </Badge>
                                                    </HStack>

                                                    <Divider />

                                                    <VStack spacing={2}>
                                                        <Button w="100%" size="sm" leftIcon={<MdCopyAll />} onClick={() => copyLink(form._id)}
                                                            variant="outline" colorScheme="yellow" borderRadius="xl" fontSize="sm">
                                                            نسخ رابط الطلاب
                                                        </Button>
                                                        <HStack w="100%">
                                                            <Button flex={1} size="sm" leftIcon={<MdVisibility />} onClick={() => handleViewResponses(form)}
                                                                colorScheme="blue" borderRadius="xl" fontSize="sm">
                                                                عرض الردود
                                                            </Button>
                                                            <Button flex={1} size="sm" leftIcon={<MdDownload />} onClick={() => handleDownloadExcel(form)}
                                                                colorScheme="green" borderRadius="xl" fontSize="sm">
                                                                Excel
                                                            </Button>
                                                            <IconButton size="sm" icon={<MdDelete />} colorScheme="red" variant="ghost"
                                                                borderRadius="xl" onClick={() => handleDelete(form._id)} />
                                                        </HStack>
                                                    </VStack>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </SimpleGrid>
                            )}
                        </TabPanel>
                    </TabPanels>
                </Tabs>

                {/* ── Responses Modal ── */}
                <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
                    <ModalOverlay backdropFilter="blur(4px)" />
                    <ModalContent dir="rtl" borderRadius="2xl" mx={4}>
                        <ModalHeader borderBottom="2px solid" borderBottomColor={PRIMARY}>
                            <HStack justify="space-between" w="100%">
                                <HStack>
                                    <Icon as={MdCalendarMonth} color={PRIMARY} />
                                    <Text>ردود استمارة: {selectedForm?.matiere}</Text>
                                </HStack>
                                <HStack>
                                    <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
                                        {responses.length} رد
                                    </Badge>
                                    {selectedForm && (
                                        <Button size="sm" leftIcon={<MdDownload />} colorScheme="green" borderRadius="xl"
                                            onClick={() => handleDownloadExcel(selectedForm)}>
                                            تحميل Excel
                                        </Button>
                                    )}
                                </HStack>
                            </HStack>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody py={4}>
                            {loadingResponses ? (
                                <Flex justify="center" py={8}><Spinner size="xl" color={PRIMARY} /></Flex>
                            ) : responses.length === 0 ? (
                                <Text textAlign="center" color="gray.400" py={8}>لا توجد ردود حتى الآن</Text>
                            ) : (
                                <VStack spacing={6} align="stretch">
                                    {Object.entries(groups).map(([combo, members], gIdx) => (
                                        <Box key={combo}>
                                            <HStack mb={2}>
                                                <Badge colorScheme={groupColors[gIdx % groupColors.length]} fontSize="sm" px={3} py={1}
                                                    borderRadius="full">
                                                    مجموعة {gIdx + 1}
                                                </Badge>
                                                <Text fontWeight="700" color={DARK} fontSize="sm">{combo}</Text>
                                                <Badge colorScheme="gray" borderRadius="full" px={2}>{members.length} طالب</Badge>
                                            </HStack>
                                            <TableContainer bg="white" borderRadius="xl" shadow="sm" border="1px solid" borderColor="gray.100">
                                                <Table size="sm">
                                                    <Thead bg={`${groupColors[gIdx % groupColors.length]}.50`}>
                                                        <Tr>
                                                            <Th textAlign="right">الاسم و اللقب</Th>
                                                            <Th textAlign="right">رقم الواتساب</Th>
                                                            <Th textAlign="right">الحصة الأولى</Th>
                                                            <Th textAlign="right">الحصة الثانية</Th>
                                                            <Th textAlign="right">تاريخ الإرسال</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {members.map((r, i) => (
                                                            <Tr key={r._id} bg={i % 2 === 0 ? 'white' : 'gray.50'}>
                                                                <Td fontWeight="600">{r.fullName}</Td>
                                                                <Td dir="ltr" textAlign="right">{r.whatsappNumber}</Td>
                                                                <Td><Badge colorScheme="blue" borderRadius="full">{r.session1Choice}</Badge></Td>
                                                                <Td><Badge colorScheme="purple" borderRadius="full">{r.session2Choice}</Badge></Td>
                                                                <Td color="gray.500" fontSize="xs">{new Date(r.submittedAt).toLocaleString('ar-TN')}</Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    ))}
                                </VStack>
                            )}
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </Box>
        </LayoutDashboard>
    );
}
